// Setup › Memory › Spaces and Rules (RECALL-ADMIN-MEMORY). Spaces: each space's charter, what
// agents may do in it and who knows what. Rules: the company's standing rules (the Recall rules
// screen's own list, reused) and, from the memory service, each space's always-on list, fenced
// rules, procedures and their vetoed steps. Every change here needs the memory service's admin
// act: until it opens the icons are there but wait ("Waiting on memory service"), never erroring.
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  QuickAddDialog,
  SettingsGroup,
  Textarea,
  toast,
  WorkerBadge,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BanIcon,
  PencilIcon,
  PlusIcon,
  ShieldIcon,
  Trash2Icon,
  UndoIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { WAITING, WaitingNote } from "./memory-admin-parts";
import {
  type AdminRules,
  type AdminSpaceRow,
  adminKeys,
  adminRulesQuery,
  adminSpacesQuery,
} from "./memory-queries";
import { StandingRules } from "./recall-upkeep";

const ICON =
  "size-7 shrink-0 opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100";

type Acted = { result: "done" | "waiting" | "refused"; note: string | null };

/** Toast a memory-service answer: done closes quietly, waiting/refused say why. */
function told(r: Acted, done: string) {
  if (r.result === "done") toast.add({ title: done, type: "success" });
  else toast.add({ title: r.note ?? WAITING, type: r.result === "waiting" ? "info" : "error" });
  return r.result === "done";
}

// ---------------------------------------------------------------------------------------------
// Spaces

const KIND_LABEL: Record<AdminSpaceRow["kind"], string> = {
  company: "Company",
  team: "Team",
  space: "Space",
  agent: "Agent",
};
const ACCESS_LABEL = {
  none: "No access",
  recall: "Can read",
  record: "Can read and write",
} as const;

export function SpacesTab({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(adminSpacesQuery(api, company.id));
  const [charter, setCharter] = useState<AdminSpaceRow | null>(null);
  const [access, setAccess] = useState<AdminSpaceRow | null>(null);
  if (q.isPending) return <Loading label="Loading spaces" rows={3} />;
  if (q.isError) return <LoadError what="Spaces" error={q.error} />;
  const d = q.data;
  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="admin-spaces">
      {d.service_ready ? null : <WaitingNote what="charters and agent access" />}
      <SettingsGroup title="Spaces" count={d.spaces.length}>
        <ul className="flex flex-col divide-y divide-subtle">
          {d.spaces.map((s) => (
            <li
              key={s.key}
              className="group/row flex min-w-0 items-start gap-3 px-4 py-2.5"
              data-slot="admin-space"
              data-space={s.key}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium">
                  {s.agent ? (
                    <WorkerBadge kind={s.agent.label === "DE" ? "de" : "ag"} size="sm" />
                  ) : null}
                  <span className="truncate">{s.name}</span>
                  <Badge variant="outline" className="h-5 text-[11px]">
                    {KIND_LABEL[s.kind]}
                  </Badge>
                  {s.agent_access ? (
                    <Badge variant="neutral" className="h-5 text-[11px]">
                      {ACCESS_LABEL[s.agent_access]}
                    </Badge>
                  ) : null}
                </span>
                <p className="text-[13px] text-muted-foreground">
                  {s.charter ?? (d.service_ready ? "No charter yet." : `Charter: ${WAITING}`)}
                </p>
                <p className="text-xs text-faint">
                  {s.records} {s.records === 1 ? "record" : "records"}
                  {s.owners.length ? ` · ${s.owners.join(", ")}` : ""}
                </p>
                {s.who_knows.length ? (
                  <ul className="flex flex-wrap gap-1.5" aria-label={`Who knows in ${s.name}`}>
                    {s.who_knows.map((w) => (
                      <li
                        key={`${w.kind}-${w.name}`}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs"
                      >
                        {w.kind === "agent" ? <WorkerBadge kind="de" size="sm" /> : null}
                        {w.name}
                        {w.topics.length ? (
                          <span className="text-faint">{w.topics.join(", ")}</span>
                        ) : (
                          <span className="text-faint tabular-nums">{w.writes}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <span className="flex shrink-0 items-center">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className={ICON}
                  aria-label={`Edit charter of ${s.name}`}
                  title={d.service_ready ? "Edit charter" : WAITING}
                  disabled={!s.can_tend}
                  onClick={() => setCharter(s)}
                >
                  <PencilIcon aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className={ICON}
                  aria-label={`Agent access in ${s.name}`}
                  title={d.service_ready ? "Agent access" : WAITING}
                  disabled={!s.can_tend}
                  onClick={() => setAccess(s)}
                >
                  <ShieldIcon aria-hidden="true" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </SettingsGroup>
      {charter ? (
        <CharterDialog
          api={api}
          company={company}
          space={charter}
          onClose={() => setCharter(null)}
        />
      ) : null}
      {access ? (
        <AccessDialog api={api} company={company} space={access} onClose={() => setAccess(null)} />
      ) : null}
    </div>
  );
}

function CharterDialog({
  api,
  company,
  space,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  space: AdminSpaceRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(space.charter ?? "");
  const id = useId();
  const save = useMutation({
    mutationFn: () =>
      api.client.memory.adminCharter({
        params: { co: company.id, space: space.key },
        body: { text: text.trim() },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all(company.id) });
      if (told(r, "Charter saved")) onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Charter"
      info="What this space is for and what belongs in it. Agents read it before they write here."
      description={space.name}
      submitLabel="Save charter"
      canSubmit={text.trim().length > 0}
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="charter-dialog"
      error={
        save.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(save.error, "Not saved. Try again.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={id}>Charter</FieldLabel>
        <Textarea
          id={id}
          rows={5}
          maxLength={2000}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
    </QuickAddDialog>
  );
}

function AccessDialog({
  api,
  company,
  space,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  space: AdminSpaceRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState<"none" | "recall" | "record">(space.agent_access ?? "recall");
  const id = useId();
  const save = useMutation({
    mutationFn: () =>
      api.client.memory.adminAgentAccess({
        params: { co: company.id, space: space.key },
        body: { agent_access: value },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all(company.id) });
      if (told(r, "Access saved")) onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Agent access"
      info="What agents may do in this space. People keep their own access."
      description={space.name}
      submitLabel="Save access"
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="access-dialog"
      error={
        save.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(save.error, "Not saved. Try again.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={id}>Agents</FieldLabel>
        <NativeSelect
          id={id}
          value={value}
          onChange={(e) => setValue(e.target.value as typeof value)}
        >
          <NativeSelectOption value="none">No access</NativeSelectOption>
          <NativeSelectOption value="recall">Can read</NativeSelectOption>
          <NativeSelectOption value="record">Can read and write</NativeSelectOption>
        </NativeSelect>
      </Field>
    </QuickAddDialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Rules

type RuleSpace = AdminRules["spaces"][number];
type RuleOp =
  | "always_on_add"
  | "always_on_remove"
  | "always_on_up"
  | "always_on_down"
  | "retire"
  | "lift"
  | "veto"
  | "unveto";

function useRuleAct(api: Api, company: CompanyAccess) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      space: string;
      op: RuleOp;
      title: string;
      type?: string;
      step?: number;
      reason?: string;
    }) => api.client.memory.adminRuleAct({ params: { co: company.id }, body: v }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all(company.id) });
      told(r, "Done");
    },
    onError: (e) => toast.add({ title: problemText(e, "Not changed. Try again."), type: "error" }),
  });
}

export function RulesTab({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(adminRulesQuery(api, company.id));
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="admin-rules">
      <StandingRules api={api} company={company} />
      {q.isPending ? (
        <Loading label="Loading rules" rows={2} />
      ) : q.isError ? (
        <LoadError what="Rules" error={q.error} />
      ) : !q.data.service_ready ? (
        <SettingsGroup title="Always-on, fences and procedures" data-slot="admin-rules-waiting">
          <div className="px-4 py-3">
            <WaitingNote what="always-on list, fences, lifting and vetoes" />
          </div>
        </SettingsGroup>
      ) : (
        <>
          {q.data.unavailable.length ? (
            <Notice tone="info" data-slot="admin-rules-unavailable">
              The memory service did not answer for {q.data.unavailable.join(", ")} just now.
              <Button size="sm" variant="ghost" className="ml-2" onClick={() => void q.refetch()}>
                Try again
              </Button>
            </Notice>
          ) : null}
          {q.data.truncated ? (
            <Notice tone="info" data-slot="admin-rules-truncated">
              The first spaces and rules are shown; the rest are left out.
            </Notice>
          ) : null}
          {q.data.spaces.length === 0 && q.data.unavailable.length === 0 ? (
            <EmptyState variant="compact" title="No rules in memory yet" />
          ) : (
            q.data.spaces.map((s) => (
              <SpaceRules key={s.key} api={api} company={company} space={s} />
            ))
          )}
        </>
      )}
    </div>
  );
}

function SpaceRules({
  api,
  company,
  space: s,
}: {
  api: Api;
  company: CompanyAccess;
  space: RuleSpace;
}) {
  const act = useRuleAct(api, company);
  const [adding, setAdding] = useState(false);
  const run = (op: RuleOp, title: string, type?: string, step?: number) =>
    act.mutate({ space: s.key, op, title, ...(type ? { type } : {}), ...(step ? { step } : {}) });
  return (
    <SettingsGroup
      title={s.name}
      data-slot="admin-space-rules"
      actions={
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Add to always-on in ${s.name}`}
          title="Add to always-on"
          disabled={s.always_on.length >= s.always_on_limit}
          onClick={() => setAdding(true)}
        >
          <PlusIcon aria-hidden="true" />
        </Button>
      }
    >
      <div className="flex flex-col gap-3 px-4 py-3">
        <section className="flex flex-col gap-1" aria-label="Always on">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">
            Always on ({s.always_on.length}/{s.always_on_limit})
          </h3>
          {s.always_on.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">Nothing is always on here.</p>
          ) : (
            <ol className="flex flex-col">
              {s.always_on.map((i, n) => (
                <li
                  key={`${i.type}-${i.title}`}
                  className="group/row flex items-center gap-2 py-1 text-[13px]"
                >
                  <span className="w-5 text-xs text-faint tabular-nums">{i.order}</span>
                  <Badge variant="outline" className="h-5 text-[11px]">
                    {i.type}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate">{i.title}</span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={ICON}
                    aria-label={`Move ${i.title} up`}
                    disabled={n === 0}
                    onClick={() => run("always_on_up", i.title, i.type)}
                  >
                    <ArrowUpIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={ICON}
                    aria-label={`Move ${i.title} down`}
                    disabled={n === s.always_on.length - 1}
                    onClick={() => run("always_on_down", i.title, i.type)}
                  >
                    <ArrowDownIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={ICON}
                    aria-label={`Remove ${i.title} from always-on`}
                    onClick={() => run("always_on_remove", i.title, i.type)}
                  >
                    <Trash2Icon aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ol>
          )}
        </section>
        <section className="flex flex-col gap-1" aria-label="Fenced rules">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Rules</h3>
          {s.rules.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">No rules here.</p>
          ) : (
            <ul className="flex flex-col">
              {s.rules.map((r) => (
                <li key={r.title} className="group/row flex items-start gap-2 py-1 text-[13px]">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="font-medium">{r.title}</span>
                    {r.text ? <span className="text-muted-foreground">{r.text}</span> : null}
                    {r.applies_to.length || r.except.length ? (
                      <span className="text-xs text-faint">
                        {r.applies_to.length ? `Applies to ${r.applies_to.join(", ")}` : ""}
                        {r.except.length ? ` · except ${r.except.join(", ")}` : ""}
                      </span>
                    ) : null}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={ICON}
                    aria-label={`Lift ${r.title}`}
                    title="Lift: stop it binding, keep it"
                    onClick={() => run("lift", r.title, r.type)}
                  >
                    <UndoIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={ICON}
                    aria-label={`Retire ${r.title}`}
                    title="Retire"
                    onClick={() => run("retire", r.title, r.type)}
                  >
                    <BanIcon aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
        {s.procedures.length ? (
          <section className="flex flex-col gap-2" aria-label="Procedures">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Procedures</h3>
            {s.procedures.map((p) => (
              <div key={p.title} className="flex flex-col gap-0.5">
                <span className="text-[13px] font-medium">{p.title}</span>
                <ol className="flex flex-col">
                  {p.steps.map((st) => (
                    <li key={st.n} className="group/row flex items-center gap-2 text-[13px]">
                      <span className="w-5 text-xs text-faint tabular-nums">{st.n}</span>
                      <span
                        className={
                          st.vetoed ? "flex-1 text-muted-foreground line-through" : "flex-1"
                        }
                      >
                        {st.text}
                      </span>
                      {st.vetoed ? (
                        <Badge variant="warning" className="h-5 text-[11px]">
                          Vetoed
                        </Badge>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className={ICON}
                        aria-label={`${st.vetoed ? "Allow" : "Veto"} step ${st.n} of ${p.title}`}
                        title={st.vetoed ? "Allow this step" : "Veto this step"}
                        onClick={() => run(st.vetoed ? "unveto" : "veto", p.title, undefined, st.n)}
                      >
                        {st.vetoed ? (
                          <UndoIcon aria-hidden="true" />
                        ) : (
                          <BanIcon aria-hidden="true" />
                        )}
                      </Button>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </section>
        ) : null}
      </div>
      {adding ? (
        <AddAlwaysOnDialog api={api} company={company} space={s} onClose={() => setAdding(false)} />
      ) : null}
    </SettingsGroup>
  );
}

function AddAlwaysOnDialog({
  api,
  company,
  space,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  space: RuleSpace;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [type, setType] = useState("Constraint");
  const [title, setTitle] = useState("");
  const id = useId();
  const tid = useId();
  const add = useMutation({
    mutationFn: () =>
      api.client.memory.adminRuleAct({
        params: { co: company.id },
        body: { space: space.key, op: "always_on_add", type, title: title.trim() },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all(company.id) });
      if (told(r, "Added")) onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Add to always-on"
      info="Always-on notes are in every briefing for this space. Keep the list short."
      description={space.name}
      submitLabel="Add"
      canSubmit={title.trim().length > 0}
      pending={add.isPending}
      onSubmit={() => add.mutate()}
      onClose={onClose}
      slot="always-on-dialog"
      error={
        add.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(add.error, "Not added. Try again.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={id}>Type</FieldLabel>
        <NativeSelect id={id} value={type} onChange={(e) => setType(e.target.value)}>
          {["Constraint", "Principle", "Decision", "Procedure", "Lesson", "Note"].map((t) => (
            <NativeSelectOption key={t} value={t}>
              {t}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <FieldLabel htmlFor={tid}>Title of the note</FieldLabel>
        <Input id={tid} maxLength={300} value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
    </QuickAddDialog>
  );
}
